import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ClientTestimonial from '../components/ClientTestimonial';
import SEO from '../components/SEO';
import { ArrowRight, TrendingUp, Users, Target, Crown, CheckCircle, FileText, Layers, Sparkles } from 'lucide-react';

const impactASPlusLogo = "/opt/brand/impactas-final-2.webp";

const whyCapabilityMatters = [
  "Strengthen leadership pipelines and succession",
  "Improve execution and accountability",
  "Build leadership resilience under pressure",
  "Align behaviour across levels and functions",
  "Translate strategy and values into daily practice"
];

const approachAttributes = [
  { label: "Insight-led", description: "Shaped by diagnostic evidence, not assumptions" },
  { label: "Applied", description: "Focused on behaviour, decisions, and real work challenges" },
  { label: "Contextual", description: "Grounded in African and organisational realities" },
  { label: "Integrated", description: "Aligned with coaching, advisory, and organisational priorities" },
  { label: "Scalable", description: "Designed to reach cohorts without diluting quality" }
];

const programmes = [
  {
    name: "Aspire™",
    icon: Sparkles,
    audience: "High-potential individuals and non-managers preparing for leadership roles.",
    focus: [
      "Leadership identity and self-awareness",
      "Transitioning from individual contribution to influence",
      "Building confidence, judgement, and accountability"
    ],
    outcomes: "Accelerated readiness for first leadership roles, clearer succession pipelines, and reduced reliance on external hiring."
  },
  {
    name: "Rise™",
    icon: TrendingUp,
    audience: "First-time and frontline managers.",
    focus: [
      "Managing self and others effectively",
      "Communication, delegation, and feedback",
      "Building engaged, productive teams"
    ],
    outcomes: "Stronger team performance, higher engagement, and reduced attrition at the frontline."
  },
  {
    name: "Elevate™",
    icon: Target,
    audience: "Senior managers and Assistant General Managers operating under pressure.",
    focus: [
      "Leadership effectiveness in complex environments",
      "Cross-functional collaboration",
      "Decision-making, influence, and resilience"
    ],
    outcomes: "Improved execution discipline, stronger collaboration, and leadership resilience under sustained pressure."
  },
  {
    name: "Lead™",
    icon: Crown,
    audience: "ExCo members, General Managers, and senior leaders shaping culture and strategy.",
    focus: [
      "Strategic leadership and enterprise thinking",
      "Culture-shaping behaviour and tone from the top",
      "Alignment between leadership, strategy, and governance"
    ],
    outcomes: "Greater strategic clarity, stronger institutional leadership, and improved organisational alignment."
  }
];

const programmeDifferentiators = [
  "Diagnostic insight (via Impact AS Plus™)",
  "Facilitated learning sessions",
  "Applied case work drawn from participants' real challenges",
  "Reflection and peer learning",
  "Targeted coaching support where appropriate"
];

const integrationOptions = [
  "Informed by RESILIGENT proprietary diagnostics (DISC Lens Africa™, Leadership Mirror™, CulturePulse™)",
  "Supported by coaching via our accredited coaching faculty",
  "Aligned with broader advisory or organisational change work"
];

const typicalEngagements = [
  "Design and deliver leadership development cohorts",
  "Refresh or rebuild internal leadership programmes",
  "Support succession and talent pipelines",
  "Embed leadership capability as part of wider transformation"
];

const lastingCapabilityOutcomes = [
  "Changes behaviour",
  "Improves leadership judgement",
  "Strengthens execution",
  "Aligns leaders around shared expectations"
];

const CapabilityBuildingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Leadership Training Programmes Nigeria | Custom Corporate Training Lagos"
        description="Bespoke leadership training and capability building programmes in Nigeria. Custom-designed management development, leadership academies, and corporate training for Nigerian organisations. Not off-the-shelf, built for your strategy and culture."
        keywords="leadership training Nigeria, corporate training Lagos, management development programme Nigeria, leadership academy Nigeria, executive training Lagos, capability building Nigeria, leadership programme design Africa, custom corporate training Nigeria, in-house training Lagos"
        canonical="/capability-building"
      />
      <Header />
      
      <main>
        {/* Hero Section with Background Image */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.8) 40%, rgba(10, 10, 10, 0.6) 70%, rgba(10, 10, 10, 0.85) 100%), url('/opt/photo/whatsapp-image-2025-12-30-at-01-16-15.webp')`,
            backgroundPosition: 'center 50%'
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-8">
                Capability Building and Training
              </h1>
              
              <p className="text-[#2693d0] text-lg lg:text-xl font-medium leading-relaxed mb-6">
                Bespoke capability building programmes designed to shift leadership performance at scale
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-6">
                RESILIGENT designs and delivers bespoke capability building programmes that strengthen leadership effectiveness, improve execution, and build organisational resilience.
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg xl:text-xl leading-relaxed max-w-2xl">
                We do not deliver calendar-based or off-the-shelf training. Our programmes are designed around your organisational context, leadership realities, and strategic priorities, and are informed by diagnostic insight rather than generic curricula.
              </p>
            </div>
          </div>
        </section>

        {/* Why Capability Building Still Matters */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Why Capability Building Still Matters
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  Organisations invest in capability building when they need to:
                </p>
                <ul className="space-y-3">
                  {whyCapabilityMatters.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="lg:pt-8">
                <div className="bg-[#f0f5f8] rounded-xl p-8 border border-slate-100 mb-6">
                  <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed mb-4">
                    The challenge is not access to content.
                  </p>
                  <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                    It is relevance, application, and sustained impact.
                  </p>
                </div>
                <div className="bg-[#2693d0]/5 rounded-xl p-6 border border-[#2693d0]/20">
                  <p className="text-slate-800 font-semibold text-base lg:text-lg leading-relaxed">
                    That is where most training fails, and where our work begins.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Approach to Capability Building */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Our Approach to Capability Building
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                Capability building at RESILIGENT is:
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {approachAttributes.map((attr, index) => (
                <div key={index} className="bg-white rounded-xl p-6 card-pulse-border">
                  <p className="text-[#2693d0] font-semibold text-lg mb-2">{attr.label}</p>
                  <p className="text-slate-600 text-sm leading-relaxed">{attr.description}</p>
                </div>
              ))}
            </div>
            
            <div className="max-w-3xl">
              <div className="bg-white rounded-xl p-6 card-pulse-border">
                <p className="text-slate-800 font-semibold text-lg lg:text-xl leading-relaxed mb-2">
                  Learning is not an event.
                </p>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  It is a mechanism for organisational change.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Bespoke Leadership Development Programmes */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mb-16">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Bespoke Leadership Development Programmes
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                RESILIGENT designs leadership development programmes across career stages and leadership levels. Each programme is tailored to organisational needs, maturity, and context.
              </p>
            </div>
            
            <div className="space-y-8">
              {programmes.map((programme, index) => {
                const Icon = programme.icon;
                return (
                  <div key={index} className="bg-[#f0f5f8] rounded-2xl p-8 lg:p-10 border border-slate-100">
                    <div className="grid lg:grid-cols-3 gap-8">
                      {/* Programme Header */}
                      <div>
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                            <Icon className="w-6 h-6 text-[#2693d0]" />
                          </div>
                          <h3 className="text-slate-800 font-semibold text-2xl">{programme.name}</h3>
                        </div>
                        <div className="mb-4">
                          <p className="text-[#2693d0] text-sm font-medium uppercase tracking-wide mb-2">Audience</p>
                          <p className="text-slate-600 text-base leading-relaxed">{programme.audience}</p>
                        </div>
                      </div>
                      
                      {/* Focus */}
                      <div>
                        <p className="text-[#2693d0] text-sm font-medium uppercase tracking-wide mb-3">Focus</p>
                        <ul className="space-y-2">
                          {programme.focus.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2 flex-shrink-0" />
                              <span className="text-slate-600 text-sm leading-relaxed">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Outcomes */}
                      <div>
                        <p className="text-[#2693d0] text-sm font-medium uppercase tracking-wide mb-3">Outcomes</p>
                        <p className="text-slate-700 text-sm leading-relaxed">{programme.outcomes}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* What Makes Our Programmes Different */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                  What Makes Our Programmes Different
                </h2>
                <p className="text-slate-200 text-base lg:text-lg leading-relaxed mb-6">
                  RESILIGENT programmes are not content-heavy.
                </p>
                <p className="text-slate-300 text-lg lg:text-xl font-medium leading-relaxed">
                  They are experience-rich, insight-driven, and behaviour-focused.
                </p>
              </div>
              
              <div>
                <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-6">
                  They may include:
                </p>
                <ul className="space-y-4 mb-8">
                  {programmeDifferentiators.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2693d0] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-200 text-base leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <p className="text-slate-200 font-medium text-base leading-relaxed">
                    The design is always driven by what needs to change, not what is easiest to deliver.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Client Testimonial - Dr. Olasupo Olusi */}
            <div className="mt-16 max-w-3xl mx-auto">
              <ClientTestimonial
                quote="The programme surfaced leadership issues that might otherwise have remained unspoken. It raised awareness across the organisation in a meaningful and constructive way."
                name="Dr. Olasupo Olusi"
                title="Managing Director"
                company="Bank of Industry"
                variant="dark"
              />
            </div>
          </div>
        </section>

        {/* Integration with Diagnostics, Coaching, and Advisory */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Integration with Diagnostics, Coaching, and Advisory
              </h2>
              
              <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed mb-8">
                Capability building does not sit in isolation.
              </p>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Depending on the engagement, programmes may be:
              </p>
              
              <ul className="space-y-4 mb-8">
                {integrationOptions.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="bg-[#2693d0]/5 rounded-xl p-6 border border-[#2693d0]/20">
                <p className="text-slate-800 font-medium text-base lg:text-lg leading-relaxed">
                  This integration ensures learning translates into measurable behavioural and performance shifts.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How Organisations Typically Engage */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  How Organisations Typically Engage
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Organisations typically engage us to:
                </p>
              </div>
              
              <div>
                <ul className="space-y-4 mb-8">
                  {typicalEngagements.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Programmes may run as standalone engagements or as part of longer-term leadership strategies.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* From Learning to Lasting Capability */}
        <section className="bg-white py-20 lg:py-28 border-t border-slate-100">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  From Learning to Lasting Capability
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Capability building only delivers value when it:
                </p>
              </div>
              
              <div>
                <ul className="space-y-4 mb-8">
                  {lastingCapabilityOutcomes.map((item, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100">
                  <p className="text-slate-800 font-medium text-base lg:text-lg leading-relaxed">
                    RESILIGENT works with organisations to ensure that learning is absorbed, applied, and reinforced, not forgotten once the programme ends.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#e8f2f7] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Next Steps
              </h2>
              
              {/* Primary CTA */}
              <div className="bg-white card-pulse-border rounded-xl p-8 mb-6 shadow-sm">
                <h3 className="text-slate-800 font-semibold text-xl mb-4">
                  Discuss a capability building programme
                </h3>
                <p className="text-slate-600 text-base leading-relaxed mb-6">
                  A conversation to explore leadership priorities, target populations, and how a bespoke programme could support your organisation.
                </p>
                <a
                  href="mailto:info@resiligent.com?subject=Capability%20Building%20Programme%20Enquiry"
                  className="inline-flex items-center gap-2 bg-[#2693d0] text-white px-6 py-3.5 rounded font-medium text-base hover:bg-[#1e7ab8] transition-all duration-300"
                >
                  Start a Conversation
                  <ArrowRight size={18} />
                </a>
              </div>

              {/* Secondary CTA */}
              <a
                href="/impact-as-plus"
                className="inline-flex items-center gap-3 text-slate-600 hover:text-[#2693d0] transition-colors duration-300"
              >
                <FileText className="w-5 h-5" />
                <span className="text-base font-medium">Explore Impact AS Plus™</span>
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>

        {/* Footer Note */}
        <section className="bg-[#e8f2f7] pb-12 border-t border-slate-200">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-8">
            <p className="text-slate-500 text-sm">
              All capability building programmes are designed and delivered by experienced practitioners and governed by professional and organisational standards.
            </p>
          </div>
        </section>
      </main>

      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default CapabilityBuildingPage;
